/**
 * /api/v1/pressure-testing — bundle 1 (fieldiq-api#907): instruments,
 * calibrations, tenant settings. Typed to the payload `pressure-testing.routes.ts`
 * returns: raw snake_case rows, every body `{ success, data, meta }`.
 *
 * PRESSURES ARRIVE IN THE TENANT'S UNIT. The API stores kPa and converts on the
 * way out (`range_min` / `range_max` are psi for an imperial tenant, kPa for a
 * metric one, `meta.pressure_unit` says which) — and on the way IN: a spec
 * write takes the tenant's unit too. The client converts in neither direction.
 */

export type PtUnitSystem = 'metric' | 'imperial';
export type PtPressureUnit = 'kPa' | 'psi';

export const PT_INSTRUMENT_KINDS = ['gauge', 'recorder', 'pump', 'transducer'] as const;
export type PtInstrumentKind = (typeof PT_INSTRUMENT_KINDS)[number];

/** The API's computed status. `never` is no calibration on record. */
export const PT_CALIBRATION_STATUSES = ['overdue', 'due_soon', 'valid', 'never'] as const;
export type PtCalibrationStatus = (typeof PT_CALIBRATION_STATUSES)[number];

export const PT_CERT_FORMATS = ['absa', 'generic'] as const;
export type PtCertFormat = (typeof PT_CERT_FORMATS)[number];

export interface PtMeta {
  unit_system: PtUnitSystem | null;
  /** `country_fallback` when companies.unit_system is NULL and the country decided. */
  unit_system_source: 'company' | 'country_fallback' | null;
  pressure_unit: PtPressureUnit | null;
  time_zone: string | null;
  /** The tenant's calendar day, 'YYYY-MM-DD'. */
  today: string | null;
  cal_warn_days: number[] | null;
  /**
   * PT-5's routes (rates, tickets) answer `{ time_zone, today, currency }`: the
   * tenant's billing currency. Absent on every other PT read, where it is null.
   */
  currency?: PtCurrency | null;
}

export interface PtEnvelope<T> {
  success: boolean;
  data: T;
  meta: PtMeta;
  warnings?: string[];
}

export interface PtResult<T> {
  data: T;
  meta: PtMeta;
}

export interface PtSpecs {
  id: string;
  instrument_kind: PtInstrumentKind | string;
  /** In the tenant's pressure unit, rounded to 0.1 by the API. Null is "not recorded". */
  range_min: number | null;
  range_max: number | null;
  accuracy_class: string | null;
  recorder_channels: number | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface PtLatestCalibration {
  id: string;
  calibrated_on: string;
  due_on: string;
  certificate_number: string | null;
  performed_by: string | null;
}

export interface PtInstrument {
  /** The equipment row's id — an instrument IS an equipment row with a specs row. */
  id: string;
  name: string;
  type: string | null;
  serial_number: string | null;
  manufacturer: string | null;
  model: string | null;
  /** The equipment status (available, in_use, retired, …), not the calibration status. */
  status: string | null;
  specs: PtSpecs;
  latest_calibration: PtLatestCalibration | null;
  calibration_status: PtCalibrationStatus | string;
  /** Negative when overdue, null when never calibrated. */
  days_until_due: number | null;
  alert_tier: number | null;
}

export interface PtCalibration {
  id: string;
  equipment_id: string;
  calibrated_on: string;
  due_on: string;
  certificate_number: string | null;
  performed_by: string | null;
  notes: string | null;
  created_by: string | null;
  created_by_name: string | null;
  created_at: string | null;
}

export interface PtInstrumentDetail extends PtInstrument {
  calibrations: PtCalibration[];
}

export interface PtSettings {
  cert_prefix: string;
  cert_format: PtCertFormat | string;
  cal_warn_days: number[];
  /** PT-5: the ticket mode a customer with no setting of its own gets. */
  default_ticket_mode?: PtTicketMode | string;
  /** False when the tenant has no row and these are the API's defaults. */
  configured: boolean;
  created_at: string | null;
  updated_at: string | null;
}

/** POST /instruments — step two of a create; step one is POST /equipment. */
export interface PtInstrumentCreate {
  equipment_id: string;
  instrument_kind: PtInstrumentKind;
  range_min?: number | null;
  range_max?: number | null;
  accuracy_class?: string | null;
  recorder_channels?: number | null;
}

/** PATCH /instruments/:id/specs — partial; an omitted key is left as it is. */
export type PtSpecsPatch = Partial<Omit<PtInstrumentCreate, 'equipment_id'>>;

export interface PtCalibrationCreate {
  calibrated_on: string;
  due_on: string;
  certificate_number?: string | null;
  performed_by?: string | null;
  notes?: string | null;
}

export type PtSettingsUpdate = Partial<Pick<PtSettings, 'cert_prefix' | 'cal_warn_days'>> & {
  cert_format?: PtCertFormat;
  default_ticket_mode?: PtTicketMode;
};

// ─── Bundle 2 (fieldiq-api#915): test types, jobs, tests ─────────────────────
//
// pressure-testing-jobs.routes.ts. Raw snake_case rows like bundle 1, every
// pressure in the tenant's unit both ways. A test job is its OWN table
// (pt_jobs, PTJ-0001 per company), never the casing `jobs`.

export interface PtTestType {
  id: string;
  name: string;
  default_hold_minutes: number | null;
  /** Tenant's unit. */
  default_allowable_drop: number | null;
  requires_recorder: boolean;
  is_active: boolean;
  created_at: string | null;
  updated_at: string | null;
}

/** POST /test-types — idempotent on the name: an existing one answers 200 with `created: false`. */
export interface PtTestTypeCreate {
  name: string;
  default_hold_minutes?: number | null;
  default_allowable_drop?: number | null;
  requires_recorder?: boolean;
}

/** scheduled → active → complete, and → cancelled (with a reason) from scheduled or active. */
export const PT_JOB_STATUSES = ['scheduled', 'active', 'complete', 'cancelled'] as const;
export type PtJobStatus = (typeof PT_JOB_STATUSES)[number];

export const PT_TEST_RESULTS = ['pending', 'pass', 'fail'] as const;
export type PtTestResult = (typeof PT_TEST_RESULTS)[number];

export interface PtJob {
  id: string;
  job_number: string;
  status: PtJobStatus | string;
  customer_id: string;
  customer_name: string | null;
  customer_contact_id: string | null;
  customer_contact_name: string | null;
  customer_contact_email: string | null;
  customer_contact_phone: string | null;
  po_number: string | null;
  afe_number: string | null;
  location_name: string | null;
  uwi: string | null;
  lsd: string | null;
  province_state: string | null;
  scope: string | null;
  technician_user_id: string | null;
  technician_name: string | null;
  /**
   * PT-5: the mode snapshotted at creation (customer's, else the company
   * default). PATCH may change it until the job's first field ticket exists
   * (409 `ticket_mode_locked`).
   */
  ticket_mode?: PtTicketMode | string | null;
  /** Calendar day 'YYYY-MM-DD'. */
  scheduled_date: string | null;
  cancel_reason: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
  /** The API's tallies of this job's tests. */
  test_counts: { total: number; pending: number; pass: number; fail: number } | null;
}

export interface PtTestInstrumentRef {
  equipment_id: string;
  name: string | null;
  serial_number: string | null;
  /** The calibration snapshotted onto the test when the guard last ran. */
  calibration_id: string | null;
  calibrated_on: string | null;
  due_on: string | null;
  certificate_number: string | null;
}

export interface PtTest {
  id: string;
  job_id: string;
  test_type_id: string;
  test_type_name: string | null;
  requires_recorder: boolean;
  test_pressure: number | null;
  hold_minutes: number | null;
  start_pressure: number | null;
  end_pressure: number | null;
  /** start − end, from the API; null without both readings. */
  pressure_drop: number | null;
  allowable_drop: number | null;
  result: PtTestResult | string;
  gauge: PtTestInstrumentRef;
  recorder: PtTestInstrumentRef | null;
  /** The hold window, stamped by the SERVER (PT-3: POST /tests/:id/hold/start, /stop). */
  started_at: string | null;
  ended_at: string | null;
  /**
   * The chart photo claimed onto the test (PT-3). Null when none. The photo
   * itself is read through GET /uploads/pt_test_chart/:testId, matched on
   * `file_key` = the upload row's `s3Key`.
   */
  chart?: { file_key: string; uploaded_at: string | null; uploaded_by: string | null } | null;
  notes: string | null;
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface PtJobDetail extends PtJob {
  tests: PtTest[];
}

/** POST /jobs. Region is free text the API canonicalises (AB, Alberta, TX …). */
export interface PtJobCreate {
  customer_id: string;
  customer_contact_id?: string | null;
  po_number?: string | null;
  afe_number?: string | null;
  location_name?: string | null;
  uwi?: string | null;
  lsd?: string | null;
  province_state?: string | null;
  scope?: string | null;
  technician_user_id?: string | null;
  scheduled_date?: string | null;
  /** Omitted: the API snapshots the customer's mode (else the company default). */
  ticket_mode?: PtTicketMode;
}

/** PATCH /jobs/:id — fields and/or a status change; `cancel_reason` only with `cancelled`. */
export type PtJobPatch = Partial<PtJobCreate> & { status?: PtJobStatus; cancel_reason?: string };

/** POST /jobs/:id/tests and PATCH /tests/:id. Pressures in the tenant's unit. */
export interface PtTestWrite {
  test_type_id?: string;
  test_pressure?: number | null;
  hold_minutes?: number | null;
  start_pressure?: number | null;
  end_pressure?: number | null;
  allowable_drop?: number | null;
  result?: PtTestResult;
  gauge_equipment_id?: string;
  recorder_equipment_id?: string | null;
  notes?: string | null;
}

/** One calibration-guard failure, as the API's 400 lists them in `errors`. */
export interface PtGuardFailure {
  code: string;
  field: string;
  error: string;
}

// ─── PT-4 (fieldiq-api#921): certificates and job signatures ─────────────────

export const PT_CERTIFICATE_STATUSES = ['draft', 'issued', 'rejected'] as const;
export type PtCertificateStatus = (typeof PT_CERTIFICATE_STATUSES)[number];

/** `failure` when any test failed, else `pass` — decided by the API from the results, never here. */
export const PT_CERTIFICATE_KINDS = ['pass', 'failure'] as const;
export type PtCertificateKind = (typeof PT_CERTIFICATE_KINDS)[number];

export const PT_SIGNATURE_ROLES = ['technician', 'witness'] as const;
export type PtSignatureRole = (typeof PT_SIGNATURE_ROLES)[number];

/**
 * One review flag. Rule flags are the API's deterministic checks; `ai` flags
 * are the drafting agent's advisory notes. None of them blocks an issue.
 */
export interface PtCertificateFlag {
  code: string;
  severity: 'warning' | 'info' | string;
  message: string;
  test_id: string | null;
  source: 'rule' | 'ai' | string;
}

/** GET /certificates — one row. `flag_count` is the API's length of the stored flags. */
export interface PtCertificateListRow {
  id: string;
  job_id: string;
  /** Null until issued: a draft has no number. */
  certificate_number: string | null;
  status: PtCertificateStatus | string;
  kind: PtCertificateKind | string;
  flag_count: number | null;
  job_number: string | null;
  customer_id: string | null;
  customer_name: string | null;
  location_name: string | null;
  uwi: string | null;
  lsd: string | null;
  technician_user_id: string | null;
  completed_at: string | null;
  issued_at: string | null;
  issued_signatory_name: string | null;
  reject_reason: string | null;
  rejected_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

/**
 * A signature as the API returns it. There is NO image here: the PNG is an R2
 * key the API reads only to render the PDF. Present / missing, signer and time.
 */
export interface PtSignature {
  role: PtSignatureRole | string;
  signer_name: string;
  signed_at: string | null;
  /** The instant in the tenant's zone, rendered by the API ("2026-10-03 12:30 MDT"). */
  signed_at_local: string | null;
  captured_by: string | null;
}

export interface PtSignatures {
  technician: PtSignature | null;
  witness: PtSignature | null;
}

/** A test as the certificate states it. Pressures in the tenant's unit. */
export interface PtCertificateTest {
  id: string;
  number: number;
  test_type_name: string | null;
  test_pressure: number | null;
  hold_minutes: number | null;
  start_pressure: number | null;
  end_pressure: number | null;
  pressure_drop: number | null;
  allowable_drop: number | null;
  result: PtTestResult | string;
  hold_started_at: string | null;
  hold_ended_at: string | null;
  hold_started_at_local: string | null;
  hold_ended_at_local: string | null;
  has_chart: boolean;
  notes: string | null;
}

/** One calibration the certificate cites — the SNAPSHOT on the tests, not the instrument's latest. */
export interface PtCalibrationReference {
  role: 'gauge' | 'recorder' | string;
  equipment_id: string;
  name: string | null;
  serial_number: string | null;
  calibration_id: string;
  calibrated_on: string | null;
  due_on: string | null;
  certificate_number: string | null;
  /** The test numbers this calibration covers. */
  tests: number[];
}

export interface PtCertificateJob {
  id: string;
  job_number: string;
  status: PtJobStatus | string;
  customer_id: string;
  customer_name: string | null;
  customer_contact_name: string | null;
  customer_contact_email: string | null;
  po_number: string | null;
  afe_number: string | null;
  location_name: string | null;
  uwi: string | null;
  lsd: string | null;
  province_state: string | null;
  scope: string | null;
  technician_user_id: string | null;
  technician_name: string | null;
  completed_at: string | null;
  completed_at_local: string | null;
}

/** The send the issue made, when it was asked to email. A failed send does not un-issue. */
export interface PtCertificateEmailResult {
  sent_to: string;
  success: boolean;
  error: string | null;
}

/**
 * GET /certificates/:id, and the answer of draft / issue / reject.
 *
 * NO EMAIL HISTORY. Each send is a pt_certificate_emails row, but no read
 * returns those rows (api#921 writes them and reads them nowhere). The only
 * email result this client ever sees is `email` on the issue's own response.
 */
export interface PtCertificate {
  id: string | null;
  job_id: string;
  certificate_number: string | null;
  status: PtCertificateStatus | string | null;
  kind: PtCertificateKind | string;
  cert_format: PtCertFormat | string;
  ai_remarks: string | null;
  flags: PtCertificateFlag[];
  ai_generated_at: string | null;
  reject_reason: string | null;
  rejected_by: string | null;
  rejected_at: string | null;
  issued_by: string | null;
  issued_at: string | null;
  issued_at_local: string | null;
  issued_signatory_name: string | null;
  has_pdf: boolean;
  created_at: string | null;
  updated_at: string | null;
  job: PtCertificateJob;
  tests: PtCertificateTest[];
  calibration_reference: PtCalibrationReference[];
  signatures: PtSignatures;
  /** Only on the issue response, and only when it was asked to email. */
  email?: PtCertificateEmailResult | null;
}

/** POST /jobs/:id/signatures. `signature_data` is a PNG data URL. */
export interface PtSignatureWrite {
  role: PtSignatureRole;
  signer_name: string;
  signature_data: string;
}

// ─── PT-5 (fieldiq-api#932): rates, customer ticket mode, field tickets ──────
//
// pressure-testing-tickets.routes.ts. Raw snake_case, like bundles 1–4. Money
// arrives as numbers already rounded by the database; a missing rate is NULL
// ("No rate"), never 0, and an explicit 0 is "No charge".

export type PtCurrency = 'CAD' | 'USD';

export const PT_TICKET_MODES = ['per_job', 'per_day'] as const;
export type PtTicketMode = (typeof PT_TICKET_MODES)[number];

export const PT_RATE_KINDS = [
  'test', 'labour_hour', 'equipment_day', 'equipment_hour', 'mobilization', 'mileage_km', 'standby_hour', 'consumable',
] as const;
export type PtRateKind = (typeof PT_RATE_KINDS)[number];
/** A line's kinds: every rate kind, plus 'other' (always priced by the office). */
export type PtLineKind = PtRateKind | 'other';

export interface PtRate {
  id: string;
  customer_id: string | null;
  customer_name: string | null;
  /** 'customer' when the row is a customer override, 'default' for the company's. */
  scope: 'customer' | 'default' | string;
  kind: PtRateKind | string;
  test_type_id: string | null;
  test_type_name: string | null;
  equipment_id: string | null;
  equipment_name: string | null;
  instrument_kind: string | null;
  consumable_name: string | null;
  /** The kind's unit (hour, day, km, each), or a consumable's own (L, m3). */
  unit: string | null;
  rate: number;
  currency: PtCurrency | string;
  effective_date: string;
  expiry_date: string | null;
  /** The API's: effective on or before the tenant's today and not yet expired. */
  in_force: boolean;
  notes: string | null;
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
}

/** POST /rates. The KEY (kind + sub-key + customer) cannot change afterwards. */
export interface PtRateCreate {
  kind: PtRateKind;
  customer_id?: string | null;
  test_type_id?: string | null;
  equipment_id?: string | null;
  instrument_kind?: string | null;
  consumable_name?: string | null;
  unit?: string | null;
  rate: number;
  currency?: PtCurrency;
  effective_date?: string | null;
  expiry_date?: string | null;
  notes?: string | null;
}

/** PATCH /rates/:id — price, currency, dates, notes only (rate_key_immutable otherwise). */
export interface PtRatePatch {
  rate?: number;
  currency?: PtCurrency;
  effective_date?: string;
  expiry_date?: string | null;
  notes?: string | null;
}

/** GET /consumables — names and units for the line picker, no prices. */
export interface PtConsumable {
  name: string;
  unit: string | null;
}

export interface PtCustomerSettings {
  customer_id: string;
  customer_name: string | null;
  /** The mode in effect: the customer's own, else the company default. */
  ticket_mode: PtTicketMode | string;
  source: 'customer' | 'company_default' | string;
  customer_ticket_mode: PtTicketMode | string | null;
  company_default_ticket_mode: PtTicketMode | string;
}

export const PT_TICKET_STATUSES = ['draft', 'signed', 'approved', 'invoiced', 'void'] as const;
export type PtTicketStatus = (typeof PT_TICKET_STATUSES)[number];

/** What a line's money column reads, as the API decided it. */
export type PtLineMoney = 'priced' | 'no_charge' | 'no_quantity' | 'no_rate';

export interface PtTicketLine {
  id: string;
  ticket_id: string;
  kind: PtLineKind | string;
  /** 'auto' lines come from the job's tests, labour days and mobilization. */
  source: 'auto' | 'manual' | string;
  description: string;
  test_id: string | null;
  test_type_id: string | null;
  equipment_id: string | null;
  equipment_name: string | null;
  instrument_kind: string | null;
  consumable_name: string | null;
  work_date: string | null;
  quantity: number | null;
  unit: string | null;
  rate: number | null;
  amount: number | null;
  money: PtLineMoney | string;
  /** Why the line has no price (or "Enter the quantity."). */
  unpriced_reason: string | null;
  rate_source: 'rate_card' | 'office' | string | null;
  rate_id: string | null;
  sort_order: number | null;
  deleted_at: string | null;
  deleted_by: string | null;
  delete_reason: string | null;
  voided_at: string | null;
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface PtTicketSignature {
  signer_name: string | null;
  signer_title: string | null;
  signed_at: string | null;
  signed_by: string | null;
  has_image: boolean;
}

export interface PtTicket {
  id: string;
  ticket_number: string;
  job_id: string;
  job_number: string | null;
  job_status: PtJobStatus | string | null;
  ticket_mode: PtTicketMode | string | null;
  /** 'job' (per_job), 'day' (a closed-out day) or 'final' (per_day completion sweep). */
  scope: 'job' | 'day' | 'final' | string;
  work_date: string | null;
  status: PtTicketStatus | string;
  currency: PtCurrency | string;
  /** NULL while any live line is unpriced or the ticket has none. */
  subtotal: number | null;
  line_count: number;
  customer_id: string;
  customer_name: string | null;
  location_name: string | null;
  uwi: string | null;
  lsd: string | null;
  province_state: string | null;
  technician_user_id: string | null;
  technician_name: string | null;
  po_number: string | null;
  afe_number: string | null;
  cost_code: string | null;
  signature: PtTicketSignature | null;
  approved_by: string | null;
  approved_at: string | null;
  void_reason: string | null;
  voided_by: string | null;
  voided_at: string | null;
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface PtTicketDetail extends PtTicket {
  lines: PtTicketLine[];
  removed_lines: PtTicketLine[];
}

/** POST /field-tickets/:id/lines. `rate` is PT_DISPATCH only (403 rate_office_only). */
export interface PtLineCreate {
  kind: Exclude<PtLineKind, 'test'>;
  quantity: number;
  description?: string | null;
  equipment_id?: string | null;
  instrument_kind?: string | null;
  consumable_name?: string | null;
  unit?: string | null;
  work_date?: string | null;
  rate?: number | null;
}

/** PATCH /field-ticket-lines/:id. `rate: null` puts the line back on the rate card. */
export interface PtLinePatch {
  quantity?: number;
  description?: string;
  rate?: number | null;
}

/**
 * POST /field-tickets/:id/sign. `afe_number` and (PT-6, fieldiq-api#939)
 * `po_number` present overwrite the ticket's — absent keeps it.
 */
export interface PtTicketSign {
  signer_name: string;
  signer_title?: string | null;
  afe_number?: string | null;
  po_number?: string | null;
  cost_code?: string | null;
  signature_data: string;
}

// ─── PT-6 (fieldiq-api#939): invoices from approved field tickets ────────────
//
// pressure-testing-invoices.routes.ts. Raw snake_case, `{ success, data }` with
// NO meta — the tenant's zone comes from useTenantTimeZone, the currency from
// the invoice. Money is numbers the API rounded; tax is the stored snapshot.

/** GET /invoices/billable — one row per job with approved tickets. */
export interface PtBillableTicket {
  ticket_id: string;
  ticket_number: string;
  scope: 'job' | 'day' | 'final' | string;
  work_date: string | null;
  currency: PtCurrency | string;
  subtotal: number | null;
  po_number: string | null;
  approved_at: string | null;
}

export interface PtBillableJob {
  job_id: string;
  job_number: string;
  ticket_mode: PtTicketMode | string | null;
  province_state: string | null;
  customer_id: string;
  customer_name: string | null;
  currency: PtCurrency | string;
  /** The API's sum of the job's approved ticket subtotals. */
  subtotal: number;
  tickets: PtBillableTicket[];
}

/** POST /invoices/generate — `job_id` OR `ticket_ids`, never both (400 SELECTION_REQUIRED). */
export type PtInvoiceGenerate =
  | { job_id: string; due_date?: string | null; notes?: string | null }
  | { ticket_ids: string[]; due_date?: string | null; notes?: string | null };

export const PT_INVOICE_STATUSES = ['draft', 'sent', 'approved', 'paid', 'void'] as const;
export type PtInvoiceStatus = (typeof PT_INVOICE_STATUSES)[number];

export interface PtInvoiceLine {
  id: string;
  ticket_id: string;
  ticket_number: string;
  ticket_line_id: string | null;
  kind: PtLineKind | string;
  description: string;
  work_date: string | null;
  quantity: number;
  unit: string | null;
  rate: number;
  amount: number;
  sort_order: number;
}

export interface PtInvoiceTicket {
  id: string;
  ticket_number: string;
  status: PtTicketStatus | string;
  scope: string;
  work_date: string | null;
  signer_name: string | null;
  po_number: string | null;
  afe_number: string | null;
}

/** GET /invoices/:id (lib/pt-invoice-pdf.ts loadPtInvoice) — the one read the send and PDF also use. */
export interface PtInvoice {
  id: string;
  vertical: 'pressure_testing';
  invoice_number: string;
  status: PtInvoiceStatus | string;
  currency: PtCurrency | string;
  /** Calendar day, the tenant-local day it was raised. */
  invoice_date: string;
  due_date: string | null;
  customer_id: string;
  customer_name: string | null;
  main_contact_name: string | null;
  main_contact_email: string | null;
  accounting_email: string | null;
  customer_address: string | null;
  /** Null when the invoice bills tickets from more than one job. */
  job_id: string | null;
  job_number: string | null;
  location_name: string | null;
  uwi: string | null;
  lsd: string | null;
  po_number: string | null;
  afe_number: string | null;
  cost_code: string | null;
  notes: string | null;
  subtotal: number;
  tax_rate: number | null;
  tax_amount: number | null;
  total: number;
  jurisdiction: string | null;
  tax_rate_1_label: string | null;
  tax_rate_1_percent: number | null;
  tax_rate_1_amount: number | null;
  tax_rate_2_label: string | null;
  tax_rate_2_percent: number | null;
  tax_rate_2_amount: number | null;
  tax_rate_source: 'jurisdiction_row' | 'company_columns' | 'none' | string | null;
  tax_rate_effective_from: string | null;
  tax_fallback_reason: 'no_rows' | 'jurisdiction_unknown' | 'jurisdiction_absent' | 'not_yet_effective' | string | null;
  tax_exempt: boolean;
  pst_exempt: boolean;
  pst_exempt_number: string | null;
  sent_at: string | null;
  sent_to: string | null;
  sent_to_source: string | null;
  sent_cc: string[] | null;
  paid_at: string | null;
  void_reason: string | null;
  voided_at: string | null;
  created_by: string | null;
  created_at: string;
  tickets: PtInvoiceTicket[];
  lines: PtInvoiceLine[];
}

export interface PtInvoiceRecipientTo {
  name: string | null;
  email: string;
  source: string;
  source_label: string;
}

/** GET /invoices/:id/recipient — who a send with no `to` would address. */
export interface PtInvoiceRecipient {
  invoice_id: string;
  invoice_number: string;
  status: string;
  customer_name: string | null;
  to: PtInvoiceRecipientTo | null;
  code?: 'NO_BILLING_EMAIL' | string;
  message?: string;
  last_sent: { at: string; to: string | null; to_source: string | null; cc: string[] | null } | null;
}

export interface PtInvoiceSent {
  id: string;
  invoice_number?: string;
  status: string;
  sent_at?: string;
  sent_to: string;
  sent_to_source: string;
  sent_to_name: string | null;
  sent_cc: string[];
}

/**
 * A row of GET /unified-invoices?vertical=pressure_testing — PT-6 has no list
 * read of its own; the unified view's PT branch is the list. `source_id` is the
 * invoice's job_id (null for a multi-job invoice). `invoice_date` there is
 * `created_at`, an INSTANT; `due_date` is the DATE cast to timestamptz.
 */
export interface PtInvoiceListRow {
  id: string;
  vertical: string;
  source_type: string;
  source_id: string | null;
  invoice_number: string;
  customer_id: string;
  customer_name: string | null;
  status: string;
  invoice_date: string | null;
  due_date: string | null;
  /** numeric — node-postgres sends it as a string. */
  total: string | number | null;
  currency: string | null;
  created_at: string | null;
}
